import { useEffect, useMemo, useState } from "react";
import { whenFree } from "@/lib/popupQueue";
import { X, Sparkles, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { POINTS_EVENT } from "@/components/PointsBurst";
import { confetti, playFanfare, playTick } from "@/lib/celebrate";
import { ShareWin } from "@/components/ShareWin";

const PALETTE = ["#F43F5E", "#FFF1F4", "#EC4899", "#FFE4EA", "#D946EF", "#FFF1F4", "#FB7185", "#FFE4EA", "#F472B6", "#FFF1F4"];
const SPIN_MS = 5200;
const JACKPOT = "J";
export const OPEN_WHEEL = "itz-open-wheel";

const buildSegments = (prizes, jackpot) => [...prizes, ...prizes, ...(jackpot > 0 ? [JACKPOT] : [])];

const arc = (i, step) => {
  const a0 = ((i * step - 90) * Math.PI) / 180, a1 = (((i + 1) * step - 90) * Math.PI) / 180;
  return `M100,100 L${100 + 96 * Math.cos(a0)},${100 + 96 * Math.sin(a0)} A96,96 0 0,1 ${100 + 96 * Math.cos(a1)},${100 + 96 * Math.sin(a1)} Z`;
};

function Wheel({ rotation, segments, mult, jackpot }) {
  const step = 360 / segments.length;
  const slices = useMemo(() => segments.map((p, i) => {
    const mid = ((i + 0.5) * step - 90) * Math.PI / 180, dark = i % 2 === 0, isJ = p === JACKPOT;
    const tx = 100 + 64 * Math.cos(mid), ty = 100 + 64 * Math.sin(mid);
    return (
      <g key={i} className={isJ ? "jackpot-seg" : ""}>
        <path d={arc(i, step)} fill={isJ ? "url(#jp-gold)" : PALETTE[i % PALETTE.length]} stroke="#fff" strokeWidth="1.5" />
        <text x={tx} y={ty} transform={`rotate(${(i + 0.5) * step} ${tx} ${ty})`} textAnchor="middle" dominantBaseline="central"
          fontSize={isJ ? 15 : 20} fontWeight="800" fill={isJ ? "#7C2D12" : dark ? "#fff" : "#E11D48"} fontFamily="inherit">{isJ ? `★${jackpot * mult}` : p * mult}</text>
      </g>
    );
  }), [segments, step, mult, jackpot]);
  return (
    <div className="relative w-64 h-64 sm:w-72 sm:h-72 mx-auto">
      <div className="absolute inset-0 rounded-full wheel-glow" />
      <svg viewBox="0 0 200 200" className="relative w-full h-full drop-shadow-xl" style={{ transform: `rotate(${rotation}deg)`, transition: `transform ${SPIN_MS}ms cubic-bezier(0.12, 0.8, 0.18, 1)` }} data-testid="wheel-disc">
        <defs><linearGradient id="jp-gold" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FEF08A" /><stop offset=".5" stopColor="#FBBF24" /><stop offset="1" stopColor="#F59E0B" /></linearGradient></defs>
        <circle cx="100" cy="100" r="99" fill="#fff" />
        {slices}
        {segments.map((_, i) => { const a = (i * step - 90) * Math.PI / 180; return <circle key={i} cx={100 + 97 * Math.cos(a)} cy={100 + 97 * Math.sin(a)} r="2.6" fill="#FDE68A" className="wheel-bulb" style={{ animationDelay: `${i * 120}ms` }} />; })}
        <circle cx="100" cy="100" r="16" fill="#fff" stroke="#F43F5E" strokeWidth="4" />
      </svg>
      <div className="absolute left-1/2 -top-2 -translate-x-1/2 w-0 h-0 border-l-[14px] border-r-[14px] border-t-[26px] border-l-transparent border-r-transparent border-t-amber-400 drop-shadow-md" data-testid="wheel-pointer" />
    </div>
  );
}

// ticks that slow down with the wheel's easing
const scheduleTicks = () => {
  for (let k = 1; k <= 40; k++) { const p = 1 - Math.pow(1 - k / 40, 0.35); setTimeout(playTick, p * SPIN_MS); }
};

function Result({ res, onDone }) {
  return (
    <div className="space-y-3 w-full" data-testid="lucky-wheel-result">
      {res.jackpot
        ? <p className="font-display text-3xl font-black jackpot-title inline-flex items-center gap-2" data-testid="lucky-wheel-jackpot"><Crown className="w-7 h-7 text-amber-500 fill-amber-400" /> JACKPOT! +{res.points}</p>
        : <p className="font-display text-xl font-extrabold text-rose-600">Glückwunsch! +{res.points} {res.points === 1 ? "Punkt" : "Punkte"} 🎉</p>}
      {res.multiplier > 1 && <p className="text-xs font-bold text-fuchsia-600">Wochenend-Bonus: doppelte Punkte!</p>}
      {(res.jackpot || res.multiplier > 1) && <ShareWin text={res.jackpot ? `Ich habe gerade den JACKPOT am itzPlay-Glücksrad geknackt: +${res.points} Punkte! 👑` : `Ich habe am itzPlay-Glücksrad +${res.points} Punkte gewonnen! 🎡`} testId="wheel-share" />}
      <button onClick={onDone} data-testid="lucky-wheel-done" className="w-full h-12 rounded-full bg-slate-900 text-white font-bold active:scale-[0.98] transition-transform">Super, danke!</button>
    </div>
  );
}

export function LuckyWheel() {
  const { user, refreshUser } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [state, setState] = useState("idle");
  const [res, setRes] = useState(null);
  const segments = useMemo(() => (cfg ? buildSegments(cfg.prizes, cfg.jackpot) : []), [cfg]);

  useEffect(() => {
    if (!user) return;
    let iv;
    const t = setTimeout(() => api.get("/wheel").then(({ data }) => {
      if (!data.popup) return;
      whenFree("wheel", () => setCfg(data));
    }).catch(() => {}), 1200);
    const open = () => api.get("/wheel").then(({ data }) => (data.available ? (setState("idle"), setRes(null), setCfg(data)) : toast("Du hast heute schon gedreht – morgen wieder! 🎡"))).catch((e) => toast.error(formatErr(e)));
    window.addEventListener(OPEN_WHEEL, open);
    return () => { clearTimeout(t); clearInterval(iv); window.removeEventListener(OPEN_WHEEL, open); };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const spin = async () => {
    setState("loading");
    try {
      const { data } = await api.post("/wheel/spin");
      const options = segments.map((p, i) => ((data.jackpot ? p === JACKPOT : p === data.base) ? i : -1)).filter((i) => i >= 0);
      const step = 360 / segments.length;
      const idx = options.length ? options[Math.floor(Math.random() * options.length)] : 0;
      setState("spinning");
      setRotation(360 * 7 + (360 - (idx + 0.5) * step) + (Math.random() - 0.5) * step * 0.6);
      scheduleTicks();
      setTimeout(() => {
        setRes(data);
        setState("done");
        if (data.jackpot) { playFanfare(); confetti({ count: 140, spread: 1.8 }); setTimeout(() => confetti({ count: 100, spread: 2.2 }), 450); }
        window.dispatchEvent(new CustomEvent(POINTS_EVENT, { detail: { amount: data.points, reason: data.jackpot ? "JACKPOT am Glücksrad! 👑" : "Glücksrad-Gewinn 🎡" } }));
        refreshUser();
      }, SPIN_MS + 200);
    } catch (e) { toast.error(formatErr(e)); setCfg(null); }
  };

  const close = () => {
    if (state === "spinning" || state === "loading") return;
    if (state === "idle") api.post("/wheel/dismiss").catch(() => {});
    setCfg(null);
  };

  if (!cfg) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-rose-950/60 backdrop-blur-md wheel-fade" data-testid="lucky-wheel-modal" style={{ pointerEvents: "auto" }}>
      <div className="relative w-full max-w-sm rounded-[2rem] bg-gradient-to-b from-white to-rose-50 p-6 pt-7 text-center wheel-frame">
        {state !== "spinning" && state !== "loading" && (
          <button onClick={close} aria-label="Schließen" data-testid="lucky-wheel-close" className="absolute top-3 right-3 w-9 h-9 rounded-full bg-white/80 text-slate-500 hover:text-rose-600 flex items-center justify-center"><X className="w-5 h-5" /></button>
        )}
        <p className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-[0.2em] text-rose-500"><Sparkles className="w-3.5 h-3.5" /> Tages-Extrabonus</p>
        <h2 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">Dein Glücksrad</h2>
        {cfg.multiplier > 1
          ? <p className="mt-2 mb-4 inline-flex px-3 py-1 rounded-full bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white text-xs font-extrabold wheel-cta" data-testid="lucky-wheel-weekend">Wochenende: alle Punkte ×2 🔥</p>
          : <p className="mt-1 mb-4 text-xs font-semibold text-amber-600" data-testid="lucky-wheel-jackpot-hint">★ Mit etwas Glück knackst du den Jackpot!</p>}
        <Wheel rotation={rotation} segments={segments} mult={cfg.multiplier} jackpot={cfg.jackpot} />
        <div className="mt-6 min-h-[3.5rem] flex items-center justify-center">
          {state === "done" ? <Result res={res} onDone={() => setCfg(null)} /> : (
            <button onClick={spin} disabled={state !== "idle"} data-testid="lucky-wheel-spin" className="w-full h-14 rounded-full pink-gradient text-white text-lg font-extrabold tracking-wide shadow-lg shadow-rose-300 inline-flex items-center justify-center gap-2 active:scale-[0.97] transition-transform disabled:opacity-80 wheel-cta">
              {state === "idle" ? "Drehen" : <><Loader2 className="w-5 h-5 animate-spin" /> Das Rad dreht sich …</>}
            </button>
          )}
        </div>
        {state === "idle" && <p className="text-xs text-slate-400 mt-3">Einmal am Tag – verpasst? Unter Spiele → Glücksrad kannst du den Dreh heute noch nachholen.</p>}
      </div>
    </div>
  );
}
